// ─── src/api/gewPartnerApplications.ts ─────────────────────────
//
// Admin read-only view of GEW partner-portal applications submitted via
// /gew/2026/join. Lives next to the legacy /gew-partners interest-form
// client — the two coexist in sws-dashboard so ops can see historical
// interest-form rows alongside the current portal applications.
//
// Writes (approve / reject / request_info) are deliberately done inside
// the partners portal (magic-link auth) so reviewer identity ends up in
// the audit log. This file only reads.

import { apiClient } from "./registration";

export type ApplicationStatus =
  | "draft"
  | "under_review"
  | "info_required"
  | "approved"
  | "waitlist"
  | "rejected"
  | "suspended";

export interface GewPartnerApplicationContact {
  fullName: string;
  email: string;
  phone: string | null;
  title: string | null;
}

export interface GewPartnerApplicationSummary {
  id: string;
  status: ApplicationStatus;
  createdAt: string;
  updatedAt?: string;
  decisionAt?: string | null;
  partnerFacingReason?: string | null;
  internalNotes?: string | null;
  organization: {
    id: string;
    slug: string | null;
    nameAr: string;
    nameEn: string | null;
    type: string;
    status: string;
    tier: string | null;
    primaryCity: string | null;
    primaryGovernorate: string | null;
    logoUrl: string | null;
  } | null;
  primaryContact: GewPartnerApplicationContact | null;
  backupContact: GewPartnerApplicationContact | null;
}

export interface GewPartnerApplicationDetail
  extends GewPartnerApplicationSummary {
  sections?: {
    one?: unknown;
    two?: unknown;
    three?: unknown;
    four?: unknown;
  };
  reviews: {
    id: string;
    decision: string;
    reviewerEmail: string | null;
    partnerFacingNote: string | null;
    internalNote: string | null;
    createdAt: string;
  }[];
}

export const listGewPartnerApplications = async (
  status?: string,
): Promise<GewPartnerApplicationSummary[]> => {
  const { data } = await apiClient.get<GewPartnerApplicationSummary[]>(
    "/gew-partner-applications",
    { params: status ? { status } : undefined },
  );
  return data;
};

export const getGewPartnerApplication = async (
  id: string,
): Promise<GewPartnerApplicationDetail> => {
  const { data } = await apiClient.get<GewPartnerApplicationDetail>(
    `/gew-partner-applications/${id}`,
  );
  return data;
};
